import type { InjectionKey } from 'vue';

const EnvironmentalConfigKey: InjectionKey<EnvironmentalConfig> = Symbol('EnvironmentalConfig');

type Datacenter = {
  key: string;
  label: string | null;
  icon: string | null;
  surveyUrl: string | null;
  identityUrl: string | null;
  digitalUrl: string | null;
  dashboardUrl: string | null;
};

class EnvironmentalConfig {
  public datacenters: Record<string, Datacenter> = {};
  public datacenterPriority: string[] = [];
  public urlWhitelist: string[] = [];
  public showDebugHtmlConfig: boolean = false;

  private sharedDevFolder: string = 'app';
  private sharedDevUser = 'qa';
  private sharedDevFolderPlaceholder = '{folder}';
  private sharedDevUserPlaceholder = '{user}';

  constructor() {
    this.initSharedDevValues();
    this.initDatacenters();
    this.initDatacenterPriority();
    this.initUrlWhitelist();
  }

  getDatacenterEnvVar(envVar: string, datacenter: string): string {
    if (!envVar || !datacenter) {
      return '';
    }

    datacenter = datacenter.toLowerCase().replace(/[^a-zA-Z0-9]/g, '');
    return import.meta.env[`${envVar}${datacenter}`] || '';
  }

  private initSharedDevValues(): void {
    if (import.meta.env.MODE !== 'development') {
      return;
    }

    // example shared-dev url: https://srecore-app.login.al2.testgizmo.us/
    if (window.location.hostname.indexOf('.testgizmo.us') === -1) {
      return;
    }

    const userFolder = window.location.hostname.split('.')[0];
    const userFolderSplit = userFolder.split('-');

    this.sharedDevUser = userFolderSplit[0] || 'qa';
    this.sharedDevFolder = userFolderSplit[1] || 'app';
  }

  private updateUrlIfDev(url: string): string {
    if (import.meta.env.MODE === 'development') {
      url = url.replace(this.sharedDevFolderPlaceholder, this.sharedDevFolder);
      url = url.replace(this.sharedDevUserPlaceholder, this.sharedDevUser);

      return url;
    }

    return url;
  }

  private getDatacenterKeys(): string[] {
    return import.meta.env.VITE_DATACENTERS?.split(',') || [];
  }

  private initDatacenters(): void {
    const datacenterKeys = this.getDatacenterKeys();

    datacenterKeys.forEach((datacenter: string) => {
      const label = this.getDatacenterEnvVar('VITE_DC_LABEL_', datacenter);
      const icon = this.getDatacenterEnvVar('VITE_DC_ICON_', datacenter);

      this.datacenters[datacenter] = {
        key: datacenter,
        label: label || null,
        icon: icon || null,
        surveyUrl: this.buildSurveyUrl(datacenter),
        identityUrl: this.buildIdentityUrl(datacenter),
        digitalUrl: this.buildDigitalUrl(datacenter),
        dashboardUrl: this.buildDashboardUrl(datacenter),
      };
    });
  }

  private initDatacenterPriority(): void {
    // The datacenters are listed in priority order in the .env file
    this.datacenterPriority = this.getDatacenterKeys();
  }

  private buildSurveyUrl(datacenterKey: string): string | null {
    const surveyUrl = this.getDatacenterEnvVar('VITE_SURVEY_', datacenterKey);
    if (surveyUrl) {
      return this.updateUrlIfDev(surveyUrl);
    }

    return null;
  }

  private buildIdentityUrl(datacenterKey: string): string | null {
    const identityUrl = this.getDatacenterEnvVar('VITE_IDENTITY_', datacenterKey);
    if (identityUrl) {
      return this.updateUrlIfDev(identityUrl);
    }

    return null;
  }

  private buildDigitalUrl(datacenterKey: string): string | null {
    const digitalUrl = this.getDatacenterEnvVar('VITE_DIGITAL_', datacenterKey);
    if (digitalUrl) {
      return digitalUrl;
    }

    return null;
  }

  private buildDashboardUrl(datacenterKey: string): string | null {
    const dashboardUrl = this.getDatacenterEnvVar('VITE_DASHBOARD_', datacenterKey);
    return dashboardUrl || null;
  }

  /**
   * Adds all survey/identity/digital URLs to the whitelist
   */
  private initUrlWhitelist(): void {
    this.datacenterPriority.forEach((datacenterKey: string) => {
      const surveyUrl = this.datacenters[datacenterKey]?.surveyUrl;
      const identityUrl = this.datacenters[datacenterKey]?.identityUrl;
      const digitalUrl = this.datacenters[datacenterKey]?.digitalUrl;
      const dashboardUrl = this.datacenters[datacenterKey]?.dashboardUrl;

      if (surveyUrl) {
        this.urlWhitelist.push(surveyUrl);
      }

      if (identityUrl) {
        this.urlWhitelist.push(identityUrl);
      }

      if (digitalUrl) {
        this.urlWhitelist.push(digitalUrl);
      }

      if (dashboardUrl) {
        this.urlWhitelist.push(dashboardUrl);
      }
    });
  }

  getDatacenterPriority(override: string = ''): string[] {
    if (!override) {
      return this.datacenterPriority;
    }

    if (!this.datacenterPriority.includes(override)) {
      return this.datacenterPriority;
    }

    // Add override to the start of the list
    return [override, ...this.datacenterPriority.filter((url: string) => url !== override)];
  }

  getAuthMethodUrl(datacenterKey: string, email: string): string {
    const identityUrl = this.datacenters[datacenterKey]?.identityUrl;

    if (identityUrl && email) {
      return `${identityUrl}/pub/auth-method?email=${encodeURIComponent(email)}`;
    }

    return '';
  }

  getSurveyLegacyLoginUrl(datacenterKey: string, email: string = ''): string {
    const surveyUrl = this.datacenters[datacenterKey]?.surveyUrl;
    if (surveyUrl && email) {
      return `${surveyUrl}/login/legacy?email=${encodeURIComponent(email)}`;
    }

    return '';
  }

  getSurveySsoAliasUrl(datacenterKey: string, alias: string = ''): string {
    const surveyUrl = this.datacenters[datacenterKey]?.surveyUrl;
    if (surveyUrl && alias) {
      return `${surveyUrl}/identity-spa/check-alias`;
    }

    return '';
  }

  getSurveyAppHandoffUrl(datacenterKey: string): string {
    const identityUrl = this.datacenters[datacenterKey]?.identityUrl;
    if (identityUrl) {
      return `${identityUrl}/auth/login-complete`;
    }

    return '';
  }

  getDigitalAppHandoffUrl(datacenterKey: string): string {
    const digitalUrl = this.datacenters[datacenterKey]?.digitalUrl;
    if (digitalUrl) {
      return digitalUrl;
    }

    return '';
  }

  getUrlForAppHandoff(appKey: string, datacenterKey: string, email: string = ''): string {
    if (appKey === 'survey') {
      return this.getSurveyAppHandoffUrl(datacenterKey);
    }
    if (appKey === 'digital') {
      return this.getDigitalAppHandoffUrl(datacenterKey);
    }
    if (appKey === 'legacy_survey' && email) {
      return this.getSurveyLegacyLoginUrl(datacenterKey, email);
    }
    return '';
  }

  getAppLabel(appKey: string = ''): string {
    if (appKey === 'survey' || appKey === 'legacy_survey') {
      return 'Alchemer Survey';
    }

    if (appKey === 'digital') {
      return 'Alchemer Digital';
    }

    if (appKey === 'workflow') {
      return 'Alchemer Workflow';
    }

    return '';
  }

  getUserProfileUrl(): string {
    if (import.meta.env.VITE_ALL_ACCOUNT) {
      return `${import.meta.env.VITE_ALL_ACCOUNT}/all-user/global-user-by-auth-token`;
    }

    return '';
  }

  getAllowedMetadataApps(): string[] {
    if (!import.meta.env.VITE_ALLOWED_METADATA_APPS) return [];

    return import.meta.env.VITE_ALLOWED_METADATA_APPS.split(',');
  }
}

export { EnvironmentalConfig, EnvironmentalConfigKey };
